/**
 * Consolidates consecutive <ul> and <ol> elements into single lists
 * to fix formatting issues when copying/pasting content
 */
export class HtmlListConsolidator {
  /**
   * Consolidates consecutive list elements in HTML content
   * @param html - The HTML content to process
   * @returns The processed HTML with consolidated lists
   */
  static consolidateLists(html: string): string {
    if (!html) return html;

    // Create a temporary DOM element to manipulate the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Process UL elements
    this.consolidateListType(tempDiv, 'ul');

    // Process OL elements
    this.consolidateListType(tempDiv, 'ol');

    return tempDiv.innerHTML;
  }

  /**
   * Consolidates consecutive list elements of a specific type (ul or ol)
   * @param container - The container element
   * @param tagName - The tag name ('ul' or 'ol')
   */
  private static consolidateListType(container: Element, tagName: 'ul' | 'ol'): void {
    const lists = Array.from(container.querySelectorAll(tagName)) as (HTMLUListElement | HTMLOListElement)[];

    for (let i = 0; i < lists.length; i++) {
      const currentList = lists[i];
      const nextSibling = this.getNextSiblingElement(currentList);

      // Check if the next sibling is the same type of list
      if (nextSibling && nextSibling.tagName.toLowerCase() === tagName) {
        // Move all li elements from the next list to the current list
        const nextListItems = Array.from(nextSibling.children);
        nextListItems.forEach(item => {
          if (item.tagName.toLowerCase() === 'li') {
            currentList.appendChild(item);
          }
        });

        // Remove the now-empty next list
        nextSibling.remove();

        // Update the lists array since we removed an element
        const nextListTyped = nextSibling as HTMLUListElement | HTMLOListElement;
        const index = lists.indexOf(nextListTyped);
        if (index > -1) {
          lists.splice(index, 1);
        }

        // Recheck the current position since we may have more consecutive lists
        i--;
      }
    }
  }

  /**
   * Gets the next sibling element, skipping text nodes and empty elements
   * @param element - The current element
   * @returns The next sibling element or null
   */
  private static getNextSiblingElement(element: Element): Element | null {
    let sibling = element.nextSibling;

    while (sibling) {
      if (sibling.nodeType === Node.ELEMENT_NODE) {
        const elem = sibling as Element;
        // Skip empty div elements or elements with only whitespace
        if (elem.tagName.toLowerCase() === 'div' && (!elem.textContent || elem.textContent.trim() === '')) {
          // Check if this div only contains <br> tags or is completely empty
          const hasOnlyBrTags = Array.from(elem.children).every(child => child.tagName.toLowerCase() === 'br');

          if (hasOnlyBrTags || elem.children.length === 0) {
            sibling = sibling.nextSibling;
            continue;
          }
        }
        return elem;
      }

      // Skip text nodes that are only whitespace
      if (sibling.nodeType === Node.TEXT_NODE && sibling.textContent && sibling.textContent.trim() === '') {
        sibling = sibling.nextSibling;
        continue;
      }

      // If we encounter a non-empty text node, stop looking
      if (sibling.nodeType === Node.TEXT_NODE && sibling.textContent && sibling.textContent.trim() !== '') {
        return null;
      }

      sibling = sibling.nextSibling;
    }

    return null;
  }

  /**
   * Normalizes list items by removing unnecessary nested containers
   * @param html - The HTML content to process
   * @returns The processed HTML with normalized list items
   */
  static normalizeListItems(html: string): string {
    if (!html) return html;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Find all list items
    const listItems = tempDiv.querySelectorAll('li');

    listItems.forEach(li => {
      // Remove unnecessary paragraph tags inside list items
      const paragraphs = li.querySelectorAll('p');
      paragraphs.forEach(p => {
        // If the paragraph is the only child or has no meaningful siblings, unwrap it
        if (li.children.length === 1 || this.shouldUnwrapParagraph(p, li)) {
          this.unwrapElement(p);
        }
      });

      // Clean up empty text nodes and normalize spacing
      this.normalizeWhitespace(li);
    });

    return tempDiv.innerHTML;
  }

  /**
   * Determines if a paragraph should be unwrapped from within a list item
   * @param paragraph - The paragraph element
   * @param listItem - The parent list item
   * @returns Whether the paragraph should be unwrapped
   */
  private static shouldUnwrapParagraph(paragraph: Element, listItem: Element): boolean {
    // Count meaningful siblings (not just whitespace or br tags)
    const meaningfulSiblings = Array.from(listItem.children).filter(child => {
      if (child === paragraph) return false;
      if (child.tagName.toLowerCase() === 'br') return false;
      return true;
    });

    // Also check for text nodes that contain actual content
    const hasSignificantTextContent = Array.from(listItem.childNodes).some(node => {
      return node.nodeType === Node.TEXT_NODE && node.textContent && node.textContent.trim() !== '';
    });

    return meaningfulSiblings.length === 0 && !hasSignificantTextContent;
  }

  /**
   * Unwraps an element, moving its contents to the parent
   * @param element - The element to unwrap
   */
  private static unwrapElement(element: Element): void {
    const parent = element.parentNode;
    if (!parent) return;

    // Move all child nodes to the parent, before the element
    while (element.firstChild) {
      parent.insertBefore(element.firstChild, element);
    }

    // Remove the now-empty element
    parent.removeChild(element);
  }

  /**
   * Normalizes whitespace within an element
   * @param element - The element to normalize
   */
  private static normalizeWhitespace(element: Element): void {
    // Remove empty text nodes and normalize spacing
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

    const textNodes: Text[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
      textNodes.push(node as Text);
    }

    textNodes.forEach(textNode => {
      if (textNode.textContent?.trim() === '') {
        textNode.remove();
      }
    });
  }
}
